/**
 * Browser-held identity and keys.
 *
 * <p>Three things are kept, all in localStorage, none of them personal:
 *
 * - an anonymous session key, which is how a shortlist is recognised without an account;
 * - the opaque access key for each analysis, so a report can be reopened;
 * - the report token issued after payment, so a purchase survives a refresh.
 *
 * Every accessor tolerates storage being unavailable, because private windows and blocked site
 * data are normal, not exceptional.
 */

const SESSION_KEY = 'scanhome.session';
const ANALYSIS_KEYS = 'scanhome.analysisKeys';
const REPORT_TOKEN = 'scanhome.reportToken';

function read(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Storage is a convenience here; losing it costs the shortlist, not the product.
  }
}

function randomKey(): string {
  const cryptoApi = window.crypto;
  if (cryptoApi && typeof cryptoApi.randomUUID === 'function') {
    return cryptoApi.randomUUID();
  }
  return `rv-${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
}

/** The anonymous session key, created on first use. */
export function sessionKey(): string {
  const existing = read(SESSION_KEY);
  if (existing) {
    return existing;
  }
  const created = randomKey();
  write(SESSION_KEY, created);
  return created;
}

function analysisKeyMap(): Record<string, string> {
  const raw = read(ANALYSIS_KEYS);
  if (!raw) {
    return {};
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    return typeof parsed === 'object' && parsed !== null ? (parsed as Record<string, string>) : {};
  } catch {
    return {};
  }
}

/** Remembers the opaque key that lets this browser reopen an analysis. */
export function rememberAnalysisKey(analysisId: string, accessKey: string | null): void {
  if (!accessKey) {
    return;
  }
  const map = analysisKeyMap();
  map[analysisId] = accessKey;
  write(ANALYSIS_KEYS, JSON.stringify(map));
}

export function analysisKey(analysisId: string): string | null {
  return analysisKeyMap()[analysisId] ?? null;
}

/** The report token issued after payment, used to reopen a purchase later. */
export function reportToken(): string | null {
  return read(REPORT_TOKEN);
}

export function rememberReportToken(token: string | null): void {
  if (token) {
    write(REPORT_TOKEN, token);
  }
}
